import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Table,
  Image,
  message,
} from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { fetchTestCategory } from "./FetchTestCategory";
import { EditOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { token } from "../../constants/Constants";
import SecureImage from "../wellnessExecutive/SecureImage";
import { create_test_category, update_test_category } from "../../endpoints/API";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const TestCategory = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [categoryForm] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const isUser = useSelector((state) => state.user.user);
  const [testCategory, setTestCategory] = useState([]);

  useEffect(() => {
    const getTestCategory = async () => {
      try {
        await fetchTestCategory(setTestCategory, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error || "Failed to fetch pouch.");
      }
    };
    getTestCategory();
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
    setIsEditMode(false);
    setEditItemId(null);
    categoryForm.resetFields();
    setFileList([]);
  };

  const handleEdit = (item) => {
    setIsModalVisible(true);
    setIsEditMode(true);
    setEditItemId(item.id);
    categoryForm.setFieldsValue({
      name: item.name,
      description: item.description,
    });
    if (item.image) {
      setFileList([]);
    }
  };

  const handleCategorySubmit = async (values) => {
    try {
      const base64Files = await Promise.all(
        fileList
          .filter((file) => file.originFileObj)
          .map((file) => getBase64(file.originFileObj))
      );

      const payload = {
        ...values,
        image: base64Files[0],
        created_by: isUser?.id,
      };

      let response;
      if (isEditMode && editItemId) {
        response = await update_test_category(editItemId, payload);
      } else {
        response = await create_test_category(payload);
      }

      const responseData = response?.data;

      if (response.statusText == "OK") {
        message.success(
          isEditMode
            ? "Test category updated successfully!"
            : "Test category created successfully!"
        );
        setIsModalVisible(false);
        await fetchTestCategory(setTestCategory, baseUrl, prodTestUrl);
        categoryForm.resetFields();
      } else {
        message.error(responseData.error || "Operation failed.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error);
    }
  };

  const handleCategoryCancel = () => {
    setIsModalVisible(false);
    categoryForm.resetFields();
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files.");
    }
    return isImage;
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    try {
      const base64 = await getBase64(file);
      onSuccess("ok");
    } catch (error) {
      onError(error);
    }
  };

  const columns = [
    { title: "Name",        dataIndex: "name",        key: "name" },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (text) => <SecureImage path={text} width={100} />,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Button type="primary" onClick={showModal}>
          Create Test Category
        </Button>
      </div>

      {/* Scrollable table wrapper */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          dataSource={testCategory?.data}
          columns={columns}
          bordered
          pagination={{ pageSize: 5 }}
          rowKey={(record) => record.id}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title={isEditMode ? "Edit Test Category" : "Create Test Category"}
        visible={isModalVisible}
        onCancel={handleCategoryCancel}
        footer={null}
        width="min(520px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategorySubmit}
        >
          <Form.Item label="Image" name="image">
            <Upload
              customRequest={handleCustomRequest}
              listType="picture-circle"
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              onPreview={handlePreview}
              maxCount={1}
            >
              {fileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter category name!" }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please enter a description!" }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button type="primary" htmlType="submit">
                {isEditMode ? "Update" : "Submit"}
              </Button>
              <Button onClick={handleCategoryCancel}>Cancel</Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TestCategory;